import { FC, useMemo, useState, useCallback } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { Loader2 } from "lucide-react"

import Accordion from "../../accordion"
import { useFetchLeadCountQuery } from "../../../../redux/queries"
import { groupLeadCountByMonth } from "../../../utils/lead"
import {
  getStackedColumnChartOptions,
  ChartSeries
} from "../../charts/stacked-column-chart-options"

export interface ChartClickData {
  period: string
  viewType: "daily" | "monthly"
  category: string
  index: number
}

interface Props {
  orgId?: string
  loid?: string
  onChartClick?: (data: ChartClickData) => void
  selectedBarIndex?: number | null
}

type ViewType = "daily" | "monthly"

const LeadStats: FC<Props> = ({
  orgId,
  loid,
  onChartClick,
  selectedBarIndex
}) => {
  const [viewType, setViewType] = useState<ViewType>("monthly")

  const range = viewType === "daily" ? 90 : 360

  const { data: leadCounts = [], isFetching } = useFetchLeadCountQuery({
    range,
    org_id: orgId,
    loid
  })

  // Transform data for daily view
  const dailyChartData = useMemo(() => {
    if (isFetching || viewType !== "daily") return null

    const sorted = [...leadCounts].sort((a, b) =>
      a.timestamp_tz_date.localeCompare(b.timestamp_tz_date)
    )

    const categories = sorted.map(item => {
      const dateObj = new Date(item.timestamp_tz_date)
      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      })
    })

    const data = sorted.map(item => item.lead_count)
    const periods = sorted.map(item => item.timestamp_tz_date)

    return { categories, data, periods }
  }, [isFetching, viewType, leadCounts])

  // Transform data for monthly view
  const monthlyChartData = useMemo(() => {
    if (isFetching || viewType !== "monthly") return null

    const monthlyData = groupLeadCountByMonth(leadCounts)

    const categories = monthlyData.map(item => `${item.month} ${item.year}`)
    const data = monthlyData.map(item => item.count)
    const periods = monthlyData.map(item => `${item.year}-${item.month}`)

    return { categories, data, periods }
  }, [isFetching, viewType, leadCounts])

  const chartData = viewType === "daily" ? dailyChartData : monthlyChartData

  // Handle chart bar click
  const handlePointClick = useCallback(
    (index: number) => {
      if (!chartData || !onChartClick) return

      onChartClick({
        period: chartData.periods[index],
        viewType,
        category: chartData.categories[index],
        index
      })
    },
    [chartData, onChartClick, viewType]
  )

  // Generate chart options
  const chartOptions = useMemo(() => {
    if (!chartData) return null

    const series: ChartSeries[] = [
      {
        name: "Leads",
        data: chartData.data,
        color: "#5B93FF"
      }
    ]

    return getStackedColumnChartOptions(
      chartData.categories,
      series,
      viewType,
      handlePointClick,
      selectedBarIndex
    )
  }, [chartData, viewType, selectedBarIndex, handlePointClick])

  const hasData = chartData && chartData.data.some(v => v > 0)

  return (
    <div className="flex flex-col flex-1 mt-2 mb-4 overflow-hidden">
      <Accordion
        title="Total Leads"
        childrenClassName="px-6 pb-4"
        noBorder={true}
      >
        {/* View Toggle Buttons */}
        <div className="flex gap-2 mt-4 mb-4">
          <button
            type="button"
            onClick={() => setViewType("daily")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewType === "daily"
                ? "bg-primary-alt-400 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Daily
          </button>
          <button
            type="button"
            onClick={() => setViewType("monthly")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewType === "monthly"
                ? "bg-primary-alt-400 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Monthly
          </button>
        </div>

        {/* Chart Container */}
        <div className="bg-white rounded-md relative">
          {isFetching && (
            <div className="flex justify-center items-center py-20">
              <Loader2
                className="animate-spin h-12 w-12 text-primary-alt-400"
                aria-hidden="true"
              />
            </div>
          )}

          {!isFetching && !hasData && (
            <div className="flex justify-center items-center py-20">
              <p className="text-gray-500 text-sm">No data available</p>
            </div>
          )}

          {!isFetching && hasData && chartOptions && (
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          )}
        </div>
      </Accordion>
    </div>
  )
}

export default LeadStats
